/**
 * 多语言辅助工具函数
 * I18n helper utilities
 */

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * 多语言辅助 composable
 */
export function useI18nHelpers() {
  const { locale, t } = useI18n()
  
  /**
   * 获取本地化文本
   * 支持传入对象格式的多语言文本
   * @param {Object|String} textObj - 多语言文本对象 { zh: '中文', en: 'English', ja: '日本語', ko: '한국어' }
   * @returns {String} 当前语言的文本
   */
  const getLocalizedText = (textObj) => {
    if (!textObj) return ''
    if (typeof textObj === 'string') return textObj
    return textObj[locale.value] || textObj.zh || textObj.en || textObj.ja || textObj.ko || ''
  }
  
  /**
   * 切换语言
   * @param {String} lang - 语言代码 'zh' | 'en' | 'ja' | 'ko'
   */
  const switchLanguage = (lang) => {
    if (!['zh', 'en', 'ja', 'ko'].includes(lang)) {
      console.warn(`Unsupported language: ${lang}`)
      return
    }
    
    locale.value = lang
    localStorage.setItem('language', lang)
    
    // 更新 HTML lang 属性（SEO）
    document.documentElement.setAttribute('lang', lang)
    
    // 如果需要更新 URL 路径（如 /en/about）
    // 可以在这里添加路由跳转逻辑
  }
  
  /**
   * 获取当前语言的显示名称
   */
  const currentLanguageLabel = computed(() => {
    const labels = {
      zh: '中文',
      en: 'English',
      ja: '日本語',
      ko: '한국어'
    }
    return labels[locale.value] || 'English'
  })
  
  /**
   * 获取支持的语言列表
   */
  const supportedLanguages = [
    { code: 'en', label: 'English', nativeLabel: 'English' },
    { code: 'zh', label: '中文', nativeLabel: '中文' },
    { code: 'ja', label: '日本語', nativeLabel: '日本語' },
    { code: 'ko', label: 'Korean', nativeLabel: '한국어' }
  ]
  
  /**
   * 格式化日期（根据语言）
   * @param {Date|String} date - 日期
   * @param {Object} options - Intl.DateTimeFormat 选项
   * @returns {String} 格式化后的日期
   */
  const formatDate = (date, options = {}) => {
    if (!date) return ''
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const localeMap = {
      zh: 'zh-CN',
      en: 'en-US',
      ja: 'ja-JP',
      ko: 'ko-KR'
    }
    return new Intl.DateTimeFormat(localeMap[locale.value] || 'en-US', options).format(dateObj)
  }
  
  /**
   * 格式化数字（根据语言）
   * @param {Number} number - 数字
   * @param {Object} options - Intl.NumberFormat 选项
   * @returns {String} 格式化后的数字
   */
  const formatNumber = (number, options = {}) => {
    if (number === null || number === undefined) return ''
    const localeMap = {
      zh: 'zh-CN',
      en: 'en-US',
      ja: 'ja-JP',
      ko: 'ko-KR'
    }
    return new Intl.NumberFormat(localeMap[locale.value] || 'en-US', options).format(number)
  }
  
  /**
   * 格式化货币（根据语言）
   * @param {Number} amount - 金额
   * @param {String} currency - 货币代码（默认根据语言自动判断）
   * @returns {String} 格式化后的货币
   */
  const formatCurrency = (amount, currency) => {
    if (amount === null || amount === undefined) return ''
    
    // 根据语言自动选择货币
    const currencyMap = {
      zh: 'CNY',
      en: 'USD',
      ja: 'JPY',
      ko: 'KRW'
    }
    const currencyCode = currency || currencyMap[locale.value] || 'USD'
    
    return formatNumber(amount, {
      style: 'currency',
      currency: currencyCode
    })
  }
  
  /**
   * 检查翻译键是否存在
   * @param {String} key - 翻译键
   * @returns {Boolean}
   */
  const hasTranslation = (key) => {
    return t(key) !== key
  }
  
  /**
   * 获取翻译，如果不存在则返回默认值
   * @param {String} key - 翻译键
   * @param {String} defaultValue - 默认值
   * @returns {String}
   */
  const tWithFallback = (key, defaultValue = '') => {
    const translation = t(key)
    return translation !== key ? translation : defaultValue
  }
  
  return {
    locale,
    getLocalizedText,
    switchLanguage,
    currentLanguageLabel,
    supportedLanguages,
    formatDate,
    formatNumber,
    formatCurrency,
    hasTranslation,
    tWithFallback,
    t
  }
}

export default useI18nHelpers
